import React, { FC } from "react";
import { Table, Select, Button } from "antd";
import type { ColumnType } from "antd/lib/table";
import { TaxLabel } from "~/gql/graphql";
import { OfficeLocation, TAX_LABEL_VALUES } from "~/design-system/data-types";
import countries from "i18n-iso-countries";
import eng from "i18n-iso-countries/langs/en.json";
import { PercentageInput } from "../input-number";

import styles from "./tax-settings.module.css";

const { ["table"]: tableClassName } = styles as any;

countries.registerLocale(eng);

export type TaxSettingsProps = {
  officeLocations: OfficeLocation[];
  defaultTaxLabel: TaxLabel;
  defaultTaxLevelPercent: number;
  handleTaxLabelChange: (countryCode: string, value: TaxLabel) => void;
  handleTaxLevelChange: (countryCode: string, value: number) => void;
  handleResetToDefault: (countryCode: string) => void;
};

type TableData = {
  key: string;
  countryCode: string;
  name: string;
  taxLabel: TaxLabel | null;
  taxLevelPercent: number | null;
};

export const TaxSettings: FC<TaxSettingsProps> = ({
  officeLocations,
  defaultTaxLabel,
  defaultTaxLevelPercent,
  handleTaxLabelChange,
  handleTaxLevelChange,
  handleResetToDefault,
}) => {
  const tableColumns: ColumnType<TableData>[] = [
    {
      title: "Country",
      dataIndex: "countryCode",
      key: "countryCode",
      render: (countryCode: string) => countries.getName(countryCode, "en"),
    },
    {
      title: "Tax Label",
      dataIndex: "taxLabel",
      key: "taxLabel",
      align: "center",
      render: (value, record) => {
        return (
          <Select
            options={TAX_LABEL_VALUES.map((t) => {
              return {
                value: t,
                label: t,
              };
            })}
            value={record.taxLabel || defaultTaxLabel}
            onChange={(value) => handleTaxLabelChange(record.countryCode, value)}
          />
        );
      },
    },
    {
      title: "Tax Level",
      dataIndex: "taxLevelPercent",
      key: "taxLevelPercent",
      align: "center",
      render: (value, record) => {
        return (
          <PercentageInput
            value={record.taxLevelPercent || defaultTaxLevelPercent}
            onChange={(value) => handleTaxLevelChange(record.countryCode, value)}
          />
        );
      },
    },
    {
      title: "Reset to defaults",
      key: "reset",
      align: "center",
      render: (value, record) => {
        return (
          <Button
            type="primary"
            onClick={() => {
              handleResetToDefault(record.countryCode);
            }}
            disabled={
              (!record.taxLabel || record.taxLabel === defaultTaxLabel) &&
              (!record.taxLevelPercent || record.taxLevelPercent === defaultTaxLevelPercent)
            }
          >
            Reset
          </Button>
        );
      },
    },
  ];

  // the following assumes that the taxLabel and taxLevelPercent are the same for all office locations in the same country
  const taxDetailsByCountryCode: TableData[] = officeLocations.reduce((acc, location) => {
    const existingEntry = acc.find((entry) => entry.countryCode === location.countryCode);
    if (!existingEntry) {
      acc.push({
        key: location.countryCode,
        countryCode: location.countryCode,
        name: countries.getName(location.countryCode, "en") ?? "",
        taxLabel: location.taxLabel,
        taxLevelPercent: location.taxLevelPercent,
      });
    }
    return acc;
  }, [] as TableData[]);
  taxDetailsByCountryCode.sort((a, b) => {
    return a.name.localeCompare(b.name);
  });
  return (
    <Table className={tableClassName} dataSource={taxDetailsByCountryCode} columns={tableColumns} />
  );
};
